import { useDebouncedCallback } from 'use-debounce';

import { useAxios } from '@/hooks/useAxios';
import { useMy } from '@/hooks/useMy';
import { useRealtimeSubscription } from '@/hooks/useRealtimeSubscription';
import {
  ChatMessageRequest,
  Conversation,
  ConversationResponse,
  Message,
  TraceEvent,
} from '@/types';
import { useMutation } from '@/utils/api';

// When the WebSocket is disconnected, fall back to polling at this interval.
const FALLBACK_POLL_MS = 60_000;

export function useCreateConversation() {
  const axios = useAxios();
  const { invalidate } = useMy({ resource: 'conversation' });

  return useMutation({
    defaultErrorMessage: 'Failed to create conversation',
    errorByStatusCode: {
      503: 'Planner temporarily unavailable. Please try again in a moment.',
    },
    mutationFn: async (
      request: string | ChatMessageRequest
    ): Promise<Conversation> => {
      const body =
        typeof request === 'string'
          ? { conversationId: '', message: request }
          : {
              conversationId: request.conversationId || '',
              message: request.message,
              mode: request.mode || 'query',
            };
      const response = await axios.post<ConversationResponse>('/planner', body);

      if (!response.data.conversation) {
        throw new Error('No conversation returned from server');
      }

      return response.data.conversation;
    },
    onSuccess: () => {
      invalidate();
    },
  });
}

export function useSendMessage() {
  const axios = useAxios();

  return useMutation({
    defaultErrorMessage: 'Failed to send message',
    errorByStatusCode: {
      503: 'Planner temporarily unavailable. Please try again in a moment.',
    },
    mutationFn: async (request: ChatMessageRequest): Promise<string> => {
      const response = await axios.post<ConversationResponse>('/planner', {
        conversationId: request.conversationId,
        message: request.message,
        mode: request.mode || 'query',
      });

      if (!response.data.response?.success) {
        throw new Error('Message failed to send');
      }

      return response.data.response.response;
    },
    // No onSuccess invalidate: consumers of message/conversation queries drive
    // updates from WebSocket events via useRealtimeSubscription instead of
    // refetching on every mutation.
  });
}

export function useDeleteConversation() {
  const axios = useAxios();
  const { invalidate } = useMy({ resource: 'conversation' });

  return useMutation({
    defaultErrorMessage: 'Failed to delete conversation',
    errorByStatusCode: {
      503: 'Planner temporarily unavailable. Please try again in a moment.',
    },
    mutationFn: async (conversationId: string): Promise<void> => {
      await axios.delete('/planner', {
        data: { conversationId },
      });
    },
    onSuccess: () => {
      invalidate();
    },
  });
}

export function useGetConversations() {
  const { data, invalidate, ...rest } = useMy(
    {
      resource: 'conversation',
      query: 'parent_id:self',
    },
    { refetchInterval: FALLBACK_POLL_MS }
  );

  const debouncedInvalidate = useDebouncedCallback(() => invalidate(), 2000);

  useRealtimeSubscription({
    patterns: [{ pattern: '#conversation#', matchType: 'prefix' }],
    onUpdate: debouncedInvalidate,
  });

  const sortedConversations = (data || []).sort(
    (a: Conversation, b: Conversation) => {
      return new Date(b.created).getTime() - new Date(a.created).getTime();
    }
  );

  return {
    data: sortedConversations as Conversation[],
    invalidate,
    ...rest,
  };
}

export function useGetChildConversations(parentId?: string) {
  const { data, invalidate, ...rest } = useMy(
    { resource: 'conversation', query: `parent_id:${parentId}` },
    {
      enabled: !!parentId,
      refetchInterval: FALLBACK_POLL_MS,
    }
  );

  const debouncedInvalidate = useDebouncedCallback(() => invalidate(), 2000);

  useRealtimeSubscription({
    patterns: [{ pattern: '#conversation#', matchType: 'prefix' }],
    enabled: !!parentId,
    onUpdate: debouncedInvalidate,
  });

  const sortedChildren = (data || []).sort(
    (a: Conversation, b: Conversation) => {
      return new Date(b.created).getTime() - new Date(a.created).getTime();
    }
  );

  return { data: sortedChildren as Conversation[], invalidate, ...rest };
}

export function useGetConversationById(conversationId?: string) {
  const query = conversationId ? `#${conversationId}` : undefined;

  const { data, invalidate, ...rest } = useMy(
    { resource: 'conversation', query },
    {
      enabled: !!conversationId,
      refetchInterval: FALLBACK_POLL_MS,
    }
  );

  const debouncedInvalidate = useDebouncedCallback(() => invalidate(), 2000);

  // Scope the WS subscription to this specific conversation record.
  useRealtimeSubscription({
    patterns: conversationId
      ? [{ pattern: `#conversation#${conversationId}`, matchType: 'exact' }]
      : [],
    enabled: !!conversationId,
    onUpdate: debouncedInvalidate,
    // Subscribe rejected → this record won't self-heal via WS. Invalidate once
    // so the 60s fallback poll isn't the only recovery path; no resubscribe.
    onError: reason => {
      console.error('useGetConversationById: subscribe rejected', {
        reason,
        conversationId,
      });
      invalidate();
    },
  });

  const conversations = (data || []) as Conversation[];
  const conversation = conversations.find(
    c =>
      c.uuid === conversationId ||
      c.id === conversationId ||
      c.key?.includes(conversationId || '')
  );

  return {
    data: conversation as Conversation | undefined,
    invalidate,
    ...rest,
  };
}

export function useGetMessages(conversationId?: string) {
  const { data, invalidate, ...rest } = useMy(
    { resource: 'message', query: `#${conversationId}` },
    {
      enabled: !!conversationId,
      refetchInterval: FALLBACK_POLL_MS,
    }
  );

  const debouncedInvalidate = useDebouncedCallback(() => invalidate(), 2000);

  // Subscribe to messages for just this conversation.
  useRealtimeSubscription({
    patterns: conversationId
      ? [{ pattern: `#message#${conversationId}`, matchType: 'prefix' }]
      : [],
    enabled: !!conversationId,
    onUpdate: debouncedInvalidate,
  });

  const messages = (data || []) as Message[];

  return {
    data: messages.sort(
      (a, b) =>
        new Date(a.timestamp || '').getTime() -
        new Date(b.timestamp || '').getTime()
    ),
    invalidate,
    ...rest,
  };
}

export function useGetConversationWithMessages(conversationId: string) {
  const { data: conversations, isLoading: conversationsLoading } =
    useGetConversations();
  const { data: messages, isLoading: messagesLoading } =
    useGetMessages(conversationId);

  const conversation = conversations.find(
    c =>
      c.uuid === conversationId ||
      c.id === conversationId ||
      c.key?.includes(conversationId)
  );

  return {
    conversation,
    messages,
    isLoading: conversationsLoading || messagesLoading,
  };
}

export function useGetConversationTraces(traceId?: string) {
  const { data, isLoading, invalidate, ...rest } = useMy(
    { resource: 'event', query: traceId || '' },
    {
      enabled: !!traceId,
      refetchInterval: FALLBACK_POLL_MS,
    }
  );

  const debouncedInvalidate = useDebouncedCallback(() => invalidate(), 2000);

  // Trace events are written to the account partition, so subscribe via
  // the account-scoped connection (user: false).
  useRealtimeSubscription({
    patterns: traceId
      ? [{ pattern: `#event#${traceId}`, matchType: 'prefix' }]
      : [],
    enabled: !!traceId,
    user: false,
    onUpdate: debouncedInvalidate,
  });

  const tracesArray = Array.isArray(data)
    ? data
    : (data as { traceevents?: TraceEvent[] })?.traceevents || [];

  return {
    data: tracesArray as TraceEvent[],
    isLoading,
    invalidate,
    ...rest,
  };
}
